import React, { useEffect, useRef, useState, useMemo, memo } from 'react';
import FeatureItem from './FeatureItem';
import { filterFeaturesByPlatform } from '../utils/filterUtils';
import { SECTION_NAMES } from '../utils/constants';
import './YearSection.css';

const YearSection = memo(({ year, categories, activeFilter, activeCategoryFilters, onCategoryFilterToggle }) => {
  const [animationKey, setAnimationKey] = useState(0);
  const sectionRef = useRef(null);
  
  // Memoize animation delay to avoid recalculation on every render
  const animationDelay = useRef(Math.random() * 0.2).current;
  
  // Trigger animation when filter changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationKey(prev => prev + 1);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [activeFilter]);
  
  // Memoize the expensive filtering logic using shared utility
  const filteredCategories = useMemo(() => {
    return filterFeaturesByPlatform(categories, activeFilter, activeCategoryFilters);
  }, [categories, activeFilter, activeCategoryFilters]);

  // Don't render the section if no features match the filter
  if (Object.keys(filteredCategories).length === 0) {
    return null;
  }

  return (
    <article
      ref={sectionRef}
      className="year-section"
      data-year={year}
      style={{ '--animation-delay': animationDelay }}
      aria-labelledby={`year-${year}`}
    >
      <h2 id={`year-${year}`} className="year-title">{year}</h2>
      <div className="categories-container" key={animationKey}>
        {Object.entries(filteredCategories).map(([categoryName, features], index) => (
          <section
            key={categoryName}
            className="category-section"
            style={{
              '--category-delay': index * 0.1,
              animationDelay: `${index * 0.1}s`
            }}
            aria-labelledby={`category-${year}-${categoryName.replace(/\s+/g, '-')}`}
          >
            <h3 id={`category-${year}-${categoryName.replace(/\s+/g, '-')}`} className="category-title">{categoryName}</h3>
            <ul className="features-list">
              {features.map((feature, featureIndex) => (
                <FeatureItem
                  key={`${year}-${categoryName}-${feature.name || featureIndex}`}
                  feature={feature}
                  year={year}
                  sectionName={categoryName}
                  activeCategoryFilters={activeCategoryFilters}
                  onCategoryFilterToggle={onCategoryFilterToggle}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </article>
  );
});

YearSection.displayName = 'YearSection';

export default YearSection;